import { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useProperty } from "@/contexts/PropertyContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Wallet, Receipt, Wrench, ClipboardList, ClipboardCheck,
  Ban, Trees, Search, ParkingSquare, PawPrint, Vote, UserCircle2, Sparkles, BookOpen,
  ChevronsUpDown, LogOut, Bot, DollarSign, ScanEye, Home, Trophy, ShieldAlert,
  Menu, MessageSquare, BellRing,
} from "lucide-react";
import AIConciergeDrawer from "@/components/AIConciergeDrawer";
import { LiveIndicator, LiveNotificationsBell } from "@/components/LiveIndicator";
import ThemeToggle from "@/components/ThemeToggle";

const PORTAL = [
  { to: "/resident/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/resident/dues", label: "Pay Dues", icon: DollarSign },
  { to: "/resident/documents", label: "Documents", icon: BookOpen },
  { to: "/resident/pay", label: "Make Payment", icon: Wallet },
  { to: "/resident/ledger", label: "Account Ledger", icon: Receipt },
  { to: "/resident/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/resident/vendors", label: "Vendor Directory", icon: Trophy },
  { to: "/resident/arc", label: "ARC Requests", icon: ClipboardList },
  { to: "/resident/arc-tracker", label: "ARC Tracker", icon: ClipboardCheck },
  { to: "/resident/violations", label: "Violations", icon: Ban },
  { to: "/resident/amenities", label: "Amenities", icon: Trees },
  { to: "/resident/lost-found", label: "Lost & Found", icon: Search },
  { to: "/resident/parking", label: "Parking & Mailboxes", icon: ParkingSquare },
  { to: "/resident/pets", label: "Pet Registry", icon: PawPrint },
  { to: "/resident/voting", label: "E-Voting & Proxies", icon: Vote },
  { to: "/resident/elections", label: "Board Elections", icon: Vote },
  { to: "/resident/board-minutes", label: "Board Minutes", icon: Vote },
  { to: "/resident/perks", label: "Perks", icon: Trophy },
  { to: "/resident/forum", label: "Neighbors Forum", icon: MessageSquare },
  { to: "/resident/pending-3ds", label: "Pending 3DS", icon: ShieldAlert },
  { to: "/resident/profile", label: "My Profile", icon: UserCircle2 },
  { to: "/resident/notifications", label: "Notification Settings", icon: BellRing },
];

const AI = [
  { to: "/resident/ai/concierge", label: "AI Concierge", icon: Bot },
  { to: "/resident/ai/maintenance", label: "Maintenance AI", icon: Wrench },
  { to: "/resident/ai/payment-advisor", label: "Payment Advisor", icon: DollarSign },
  { to: "/resident/ai/arc", label: "ARC AI Assistant", icon: ClipboardList },
  { to: "/resident/ai/violation", label: "Violation Pre-Check", icon: ScanEye },
];

export default function ResidentShell() {
  const { user, logout } = useAuth();
  const { currentProperty } = useProperty();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile nav Sheet whenever the route changes (react-router picks it up automatically).
  const closeNav = () => setNavOpen(false);

  const renderNavContent = (onNav) => (
    <>
      <div className="p-5 border-b border-black/5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-zinc-900 flex items-center justify-center">
            <Home className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-[13px] leading-tight font-semibold font-display">Prestigious</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Resident Portal</div>
          </div>
        </div>
        <div className="mt-4 rounded-lg bg-zinc-100 border border-black/5 px-3 py-2.5" data-testid="resident-property-card">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Property</div>
          <div className="text-sm font-semibold mt-0.5 truncate">{currentProperty?.name || "Skyline Residences"}</div>
          <div className="text-xs text-zinc-500">Unit {user?.unit || "—"}</div>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 pb-6 scroll-slim">
        <div className="mt-6">
          <div className="overline px-4 mb-2">Resident Portal</div>
          <nav className="flex flex-col gap-0.5 px-2">
            {PORTAL.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onNav}
                  data-testid={`resnav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                  className={
                    "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors " +
                    (isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-700 hover:bg-zinc-100")
                  }
                >
                  <Icon className={"h-4 w-4 " + (isActive ? "text-white" : "text-zinc-500")} />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="mt-6">
          <div className="overline px-4 mb-2 flex items-center gap-2">
            <Sparkles className="h-3 w-3 text-indigo-500" /> AI Features
          </div>
          <nav className="flex flex-col gap-0.5 px-2">
            {AI.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onNav}
                  data-testid={`resnav-ai-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                  className={
                    "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors " +
                    (isActive
                      ? "bg-indigo-50 text-indigo-900 shadow-[inset_0_0_0_1px_rgba(79,70,229,0.25)]"
                      : "text-zinc-700 hover:bg-indigo-50/50")
                  }
                >
                  <Icon className={"h-4 w-4 " + (isActive ? "text-indigo-600" : "text-indigo-500")} />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="p-3 border-t border-black/5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 rounded-md p-2 hover:bg-zinc-100 transition-colors" data-testid="user-menu-trigger">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar || ""} alt={user?.name || ""} />
                <AvatarFallback className="bg-zinc-200 text-zinc-700">
                  {(user?.name || "R").slice(0,1)}
                </AvatarFallback>
              </Avatar>
              <div className="text-left min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">Resident</div>
              </div>
              <ChevronsUpDown className="h-4 w-4 text-zinc-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" className="w-56">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { closeNav(); setAiOpen(true); }} data-testid="menu-open-ai">
              <Sparkles className="h-4 w-4 mr-2" />Open AI Concierge
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" />Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      <div className="flex min-h-screen">
        {/* Desktop sidebar: hidden below lg (1024px) so mobile viewports get the full width. */}
        <aside className="hidden lg:flex w-72 shrink-0 border-r border-black/5 bg-white flex-col" data-testid="resident-sidebar">
          {renderNavContent(undefined)}
        </aside>

        <main className="flex-1 min-w-0 flex flex-col">
          <header className="h-14 flex items-center justify-between border-b border-black/5 px-4 sm:px-6 bg-white/70 backdrop-blur sticky top-0 z-30 gap-2" data-testid="topbar">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile menu trigger — only visible under lg. */}
              <button
                onClick={() => setNavOpen(true)}
                className="lg:hidden -ml-1 p-2 rounded-md hover:bg-zinc-100 shrink-0"
                aria-label="Open navigation"
                data-testid="mobile-nav-open"
              >
                <Menu className="h-5 w-5 text-zinc-700" />
              </button>
              <div className="text-sm text-zinc-500 truncate">
                Welcome back, <span className="text-zinc-900 font-medium">{user?.name?.split(" ")[0]}</span>
              </div>
              <LiveIndicator />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ThemeToggle variant="light" />
              <LiveNotificationsBell onClick={() => navigate("/resident/dashboard")} />
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                onClick={() => setAiOpen(true)}
                data-testid="topbar-ai-button"
              >
                <Sparkles className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">AI Concierge</span>
              </Button>
            </div>
          </header>
          <AnimatePresence mode="wait">
            <motion.div
              key={pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="p-4 sm:p-6 lg:p-8"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile navigation Sheet — slides in from the left. */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0 flex flex-col bg-white text-zinc-900" data-testid="mobile-nav-sheet">
          <SheetHeader className="sr-only">
            <SheetTitle>Resident navigation</SheetTitle>
            <SheetDescription>Navigate between dashboard, payments, maintenance, and AI assistants.</SheetDescription>
          </SheetHeader>
          {renderNavContent(closeNav)}
        </SheetContent>
      </Sheet>

      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent className="glass-light w-full sm:max-w-md p-0 flex flex-col h-full">
          <SheetHeader className="p-4 border-b border-black/5 shrink-0">
            <SheetTitle className="font-display">AI Concierge</SheetTitle>
            <SheetDescription className="sr-only">Ask the AI concierge anything about your community.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            <AIConciergeDrawer agent="concierge" theme="light" />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
